import { Component, DestroyRef, inject, signal, OnInit, PLATFORM_ID, ViewEncapsulation } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { ActivatedRoute, Router } from '@angular/router';
import { CheckoutMode, InvoicingService, IPaymentMethod, ISaleStatus } from '../../../core/services/invoicing.service';
import { ToolsService } from '../../../core/services/tools.service';
import { takeUntilDestroyed, toSignal } from '@angular/core/rxjs-interop';
import { StoresService } from '../../../core/services/stores.service';
import { TranslatePipe, TranslateService } from '@ngx-translate/core';
import { SaleDetailsComponent } from '../sale-details/sale-details.component';
import { AlertComponent } from '../../../shared-with-client/alert/alert.component';
import { LoaderComponent } from '../../../shared-with-client/loader/loader.component';
import { MerchantCurrencyPipe } from '../../../shared/pipes/merchant-currency.pipe';
import { ButtonComponent } from '../../../shared-with-client/button/button.component';
import { ITeyaPaymentStatus, TeyaService } from '../../../core/services/teya.service';
import { ToastNotificationsService } from '../../../core/services/toast-notifications.service';
import { from, Subject, switchMap, takeUntil, takeWhile, timer } from 'rxjs';
import { BsDropdownDirective, BsDropdownMenuDirective, BsDropdownToggleDirective } from 'ngx-bootstrap/dropdown';
import { environment } from '../../../../environments/environment';
import { BsModalService } from 'ngx-bootstrap/modal';

declare const Stripe: stripe.StripeStatic;

const POLL_INTERVAL_MS = 1500;
const MAX_POLL_MS = 10 * 60 * 1000;
const FINAL_PAYMENT_STATUSES = ['SUCCESSFUL', 'FAILED', 'CANCELLED'];
const SUCCESSFUL_PAYMENT_STATUS = 'SUCCESSFUL';
@Component({
    selector: 'app-invoice-success',
    imports: [
        TranslatePipe,
        AlertComponent,
        BsDropdownDirective,
        BsDropdownToggleDirective,
        BsDropdownMenuDirective,
        LoaderComponent, MerchantCurrencyPipe, ButtonComponent
    ],
    templateUrl: './invoice-success.component.html',
    styleUrl: './invoice-success.component.scss',
    encapsulation: ViewEncapsulation.None,
})
export class InvoiceSuccessComponent implements OnInit {
    private invoicingService = inject(InvoicingService);
    private toolsService = inject(ToolsService);
    private router = inject(Router);
    private modalService = inject(BsModalService);
    private storesService = inject(StoresService);
    private teyaService = inject(TeyaService);
    private toastNotificationsService = inject(ToastNotificationsService);
    private translateService = inject(TranslateService);
    private destroyRef = inject(DestroyRef);
    private activatedRoute = inject(ActivatedRoute);
    private platformId = inject(PLATFORM_ID);
    private stripe: stripe.Stripe | null = null;

    public currentStore = toSignal(this.storesService.getActiveStore());
    public completeInvoiceData = toSignal(this.invoicingService.completeInvoiceData$);
    public unchargedTeyaAmount = toSignal(this.invoicingService.unchargedTeyaAmount$);
    public pendingMbWayPayment = toSignal(this.invoicingService.pendingMbWayPayment$);

    public checkoutMode = toSignal(this.invoicingService.checkoutMode$, { initialValue: null });
    public CheckoutMode = CheckoutMode;

    public callTeyaTerminal = signal(!!this.activatedRoute.snapshot.data.teyaTerminal);
    public waitingForTeyaTerminal = signal(this.callTeyaTerminal());
    public waitingForMbWay = signal(!!this.invoicingService.pendingMbWayPayment$.value?.clientSecret);
    /** Cancel/discard only while PI is still requires_action (not processing/succeeded). */
    public mbWayCancelAllowed = signal(true);
    public teyaPaymentFailed = signal(false);
    public mbWayPaymentFailed = signal(false);
    public cancellingTeyaPayment = signal(false);

    private stopPolling$ = new Subject<void>();
    private activeTeyaPaymentRequestId: string | null = null;

    ngOnInit(): void {
        if (!this.completeInvoiceData()?.id) {
            this.goToStart();
            return;
        }

        if (this.callTeyaTerminal()) {
            this.sendSaleToTeyaTerminal();
            return;
        }

        if (this.waitingForMbWay()) {
            this.pollMbWayPaymentIntent();
        }
    }

    sendSaleToTeyaTerminal() {
        const sale = this.completeInvoiceData();
        const saleId = sale?.id;
        const amountCents = this.resolveTeyaRetryAmountCents(sale);

        if (!saleId || !amountCents) {
            this.toastNotificationsService.showToast(
                this.translateService.instant('GENERAL.generalErrorMsg'),
                'error',
            );
            this.teyaPaymentFailed.set(true);
            return;
        }

        this.waitingForTeyaTerminal.set(true);
        this.teyaPaymentFailed.set(false);
        this.activeTeyaPaymentRequestId = null;
        this.invoicingService.unchargedTeyaAmount$.next(amountCents);

        this.teyaService.initiateSalePayment(saleId, amountCents)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (result) => {
                    this.activeTeyaPaymentRequestId = result.paymentRequestId;
                    this.pollPaymentStatus(saleId, result.paymentRequestId);
                },
                error: (error) => {
                    this.toastNotificationsService.showToast(error?.errMsg || this.translateService.instant('GENERAL.generalErrorMsg'), 'error');
                    this.handleLivePaymentFailure('teya');
                },
            });
    }

    private resolveTeyaRetryAmountCents(sale: any): number {
        const stored = Number(this.unchargedTeyaAmount()) || 0;
        if (stored > 0) {
            return stored;
        }

        const due = Number(sale?.totalCents || 0) - Number(sale?.paidAmountCents || 0);

        return due > 0 ? due : 0;
    }

    getPaymentStatus(saleId: number, paymentRequestId: string) {
        return this.teyaService.getPaymentStatus(saleId, paymentRequestId);
    }

    private pollPaymentStatus(saleId: number, paymentRequestId: string): void {
        let reachedFinalStatus = false;

        timer(0, POLL_INTERVAL_MS)
            .pipe(
                switchMap(() => this.getPaymentStatus(saleId, paymentRequestId)),
                takeWhile((status) => !this.isFinalPaymentStatus(status), true),
                takeUntil(this.stopPolling$),
                takeUntil(timer(MAX_POLL_MS)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (status: ITeyaPaymentStatus) => {
                    if (this.isFinalPaymentStatus(status)) {
                        reachedFinalStatus = true;
                        if (!this.isSuccessfulPaymentStatus(status)) {
                            this.handleLivePaymentFailure(
                                'teya',
                                this.translateService.instant('SHARED.SIMPLE_PAYMENT_MODAL.paymentFailed'),
                            );
                        } else {
                            this.refreshSaleData();
                            this.teyaPaymentFailed.set(false);
                        }
                    }
                },
                error: (error) => {
                    this.handleLivePaymentFailure(
                        'teya',
                        error?.errMsg || this.translateService.instant('GENERAL.generalErrorMsg'),
                    );
                },
                complete: () => {
                    if (!reachedFinalStatus) {
                        this.handleLivePaymentFailure(
                            'teya',
                            this.translateService.instant('SHARED.SIMPLE_PAYMENT_MODAL.paymentTimeout'),
                        );
                    }
                },
            });
    }

    refreshSaleData() {
        this.invoicingService.getSaleDetails(this.completeInvoiceData()?.id, this.currentStore()?.id)
            .pipe(takeUntilDestroyed(this.destroyRef))
            .subscribe({
                next: (saleDetails) => {
                    this.applySaleDetailsToPreview(saleDetails);
                    this.waitingForTeyaTerminal.set(false);
                    this.waitingForMbWay.set(false);
                    this.invoicingService.clearPendingMbWayPayment();
                },
                error: (error) => {
                    this.toastNotificationsService.showToast(error?.errMsg || this.translateService.instant('GENERAL.generalErrorMsg'), 'error');
                    this.waitingForTeyaTerminal.set(false);
                    this.waitingForMbWay.set(false);
                },
            });
    }

    private applySaleDetailsToPreview(saleDetails: any): void {
        this.invoicingService.generateExistingSalePreview(saleDetails);
        this.invoicingService.completeInvoiceData$.next(saleDetails);
    }

    /**
     * Stripe.js can report PaymentIntent succeeded before our webhook records the payment.
     * Ephemeral MB Way/Teya also emit the invoice only after payment (tryAutoInvoiceAfterPayment),
     * so keep polling until payment is recorded and — when invoicing applies — invoicePdfUrl exists.
     */
    private refreshSaleDataUntilPaid(): void {
        const saleId = this.completeInvoiceData()?.id;
        const storeId = this.currentStore()?.id;
        const paidAmountBeforeCents = Number(this.completeInvoiceData()?.paidAmountCents) || 0;

        if (!saleId || !storeId) {
            this.waitingForMbWay.set(false);
            return;
        }

        let settled = false;

        timer(0, POLL_INTERVAL_MS)
            .pipe(
                switchMap(() => this.invoicingService.getSaleDetails(saleId, storeId)),
                takeWhile(
                    (saleDetails) => !this.isMbWayCheckoutSettled(saleDetails, paidAmountBeforeCents),
                    true,
                ),
                takeUntil(this.stopPolling$),
                takeUntil(timer(MAX_POLL_MS)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (saleDetails) => {
                    this.applySaleDetailsToPreview(saleDetails);

                    if (this.isMbWayCheckoutSettled(saleDetails, paidAmountBeforeCents)) {
                        settled = true;
                        this.waitingForMbWay.set(false);
                        this.mbWayPaymentFailed.set(false);
                        this.invoicingService.clearPendingMbWayPayment();
                    }
                },
                error: (error) => {
                    this.toastNotificationsService.showToast(
                        error?.errMsg || this.translateService.instant('GENERAL.generalErrorMsg'),
                        'error',
                    );
                    this.waitingForMbWay.set(false);
                },
                complete: () => {
                    if (!settled) {
                        // Show whatever we last know; sale/invoice may still catch up via webhook.
                        this.waitingForMbWay.set(false);
                        this.invoicingService.clearPendingMbWayPayment();
                    }
                },
            });
    }

    /** Full payment → status paid; partial → paidAmountCents must increase. */
    private isMbWayPaymentRecorded(saleDetails: any, paidAmountBeforeCents: number): boolean {
        if (!saleDetails) {
            return false;
        }

        if (saleDetails.status === ISaleStatus.PAID) {
            return true;
        }

        return Number(saleDetails.paidAmountCents || 0) > paidAmountBeforeCents;
    }

    /**
     * Success screen needs invoicePdfUrl for download/print actions.
     * Ephemeral checkout skips invoice on create and emits it only after payment.
     */
    private isMbWayCheckoutSettled(saleDetails: any, paidAmountBeforeCents: number): boolean {
        if (!this.isMbWayPaymentRecorded(saleDetails, paidAmountBeforeCents)) {
            return false;
        }

        if (!saleDetails?.invoicingWasActiveAtCreation) {
            return true;
        }

        return !!saleDetails.invoicePdfUrl || !!saleDetails.invoiceEmissionFailed;
    }

    private ensureStripe(): stripe.Stripe {
        if (!isPlatformBrowser(this.platformId) || typeof Stripe !== 'function') {
            throw new Error(this.translateService.instant('GENERAL.generalErrorMsg'));
        }

        if (!this.stripe) {
            this.stripe = Stripe(environment.stripe.publishableKey);
        }

        return this.stripe;
    }

    private pollMbWayPaymentIntent(): void {
        const pending = this.invoicingService.pendingMbWayPayment$.value;
        if (!pending?.clientSecret) {
            this.waitingForMbWay.set(false);
            return;
        }

        let reachedFinalStatus = false;
        this.waitingForMbWay.set(true);
        this.mbWayCancelAllowed.set(true);
        this.mbWayPaymentFailed.set(false);

        let stripe: stripe.Stripe;
        try {
            stripe = this.ensureStripe();
        } catch (error: any) {
            this.finishMbWayFailure(error?.message || this.translateService.instant('GENERAL.generalErrorMsg'));
            return;
        }

        timer(0, POLL_INTERVAL_MS)
            .pipe(
                switchMap(() => from(stripe.retrievePaymentIntent(pending.clientSecret))),
                takeWhile((result) => {
                    const status = result.paymentIntent?.status;
                    return status === 'requires_action' || status === 'processing';
                }, true),
                takeUntil(this.stopPolling$),
                takeUntil(timer(MAX_POLL_MS)),
                takeUntilDestroyed(this.destroyRef),
            )
            .subscribe({
                next: (result) => {
                    if (result.error) {
                        reachedFinalStatus = true;
                        this.finishMbWayFailure(
                            result.error.message || this.translateService.instant('GENERAL.generalErrorMsg'),
                        );
                        return;
                    }

                    const status = result.paymentIntent?.status;
                    if (status === 'processing') {
                        // Capture in flight — do not allow discard/cancel.
                        this.mbWayCancelAllowed.set(false);
                        return;
                    }

                    if (status === 'requires_action') {
                        this.mbWayCancelAllowed.set(true);
                        return;
                    }

                    reachedFinalStatus = true;
                    if (status === 'succeeded') {
                        // Keep MB Way waiting UI until the sale is paid in our API.
                        this.mbWayCancelAllowed.set(false);
                        this.refreshSaleDataUntilPaid();
                    } else {
                        this.finishMbWayFailure(
                            this.translateService.instant('SHARED.SIMPLE_PAYMENT_MODAL.paymentFailedDescription'),
                        );
                    }
                },
                error: (error) => {
                    this.finishMbWayFailure(
                        error?.message || this.translateService.instant('GENERAL.generalErrorMsg'),
                    );
                },
                complete: () => {
                    if (!reachedFinalStatus && this.waitingForMbWay()) {
                        this.finishMbWayFailure(
                            this.translateService.instant('SHARED.SIMPLE_PAYMENT_MODAL.paymentTimeout'),
                        );
                    }
                },
            });
    }

    async cancelMbWayWaiting(): Promise<void> {
        if (!this.mbWayCancelAllowed()) {
            return;
        }

        const pending = this.invoicingService.pendingMbWayPayment$.value;
        if (pending?.clientSecret) {
            try {
                const stripe = this.ensureStripe();
                const result = await stripe.retrievePaymentIntent(pending.clientSecret);
                const status = result.paymentIntent?.status;

                if (status === 'succeeded' || status === 'processing') {
                    this.mbWayCancelAllowed.set(false);
                    if (status === 'succeeded') {
                        this.refreshSaleDataUntilPaid();
                    }
                    // Keep waiting — do not discard a captured/processing payment.
                    return;
                }
            } catch {
                // Cannot confirm PI is still cancellable — fail closed (keep waiting).
                this.mbWayCancelAllowed.set(false);
                return;
            }
        }

        this.stopPolling$.next();
        this.handleLivePaymentFailure('mbway');
    }

    private finishMbWayFailure(message?: string): void {
        this.handleLivePaymentFailure('mbway', message);
    }

    livePaymentFailed(): boolean {
        return this.teyaPaymentFailed() || this.mbWayPaymentFailed();
    }

    private shouldDiscardEphemeralSaleOnFailure(): boolean {
        // New checkout sales for live MB Way / Teya are ephemeral; existing-sale
        // modes (ADD_PAYMENT / EDIT_SALE) must keep the sale on failure.
        return this.checkoutMode() === CheckoutMode.CREATE_FULL_SALE
            || this.completeInvoiceData()?.ephemeralCheckout === true;
    }

    /**
     * On MB Way / Teya failure:
     * - Teya: keep the sale + amount so "Tentar novamente" can re-send to the terminal
     * - MB Way ephemeral: discard the orphan sale (cart/client stay in memory)
     * - always show the failure UI with an explicit "edit and retry" CTA
     */
    private handleLivePaymentFailure(channel: 'teya' | 'mbway', message?: string): void {
        this.stopPolling$.next();
        this.waitingForTeyaTerminal.set(false);
        this.waitingForMbWay.set(false);
        this.callTeyaTerminal.set(false);
        this.activeTeyaPaymentRequestId = null;
        this.cancellingTeyaPayment.set(false);
        this.invoicingService.clearPendingMbWayPayment();

        // Keep uncharged Teya amount so retry can re-initiate the terminal payment.
        if (channel !== 'teya') {
            this.invoicingService.clearUnchargedTeyaAmount();
        }

        if (message) {
            this.toastNotificationsService.showToast(message, 'error');
        }

        if (channel === 'teya') {
            this.teyaPaymentFailed.set(true);
            // Do not discard yet — user may retry the same sale on the terminal.
            return;
        }

        this.mbWayPaymentFailed.set(true);

        if (!this.shouldDiscardEphemeralSaleOnFailure()) {
            return;
        }

        const saleId = this.completeInvoiceData()?.id;
        if (!saleId) {
            return;
        }

        this.invoicingService.discardEphemeralCheckoutSale(saleId).subscribe({
            next: () => {
                // Keep preview totals for context; sale row is already gone.
                this.invoicingService.clearCompleteInvoiceData();
            },
            error: () => {
                // Non-ephemeral / already discarded — failure UI still lets the user edit.
            },
        });
    }

    goBackToEdit(): void {
        const saleId = this.completeInvoiceData()?.id;
        const isNewCheckout = this.checkoutMode() === CheckoutMode.CREATE_FULL_SALE
            || this.completeInvoiceData()?.ephemeralCheckout === true
            || !saleId;
        const slug = this.currentStore()?.slug;

        const navigateAway = () => {
            this.invoicingService.clearCompleteInvoiceData();
            this.invoicingService.clearExistingSalePreview();
            this.invoicingService.clearUnchargedTeyaAmount();
            this.invoicingService.clearPendingMbWayPayment();

            if (!slug) {
                return;
            }

            // CREATE_FULL_SALE: cart/client stay in InvoicingService → payment form.
            // Existing sale: reopen the pay step for that sale.
            if (isNewCheckout || !saleId) {
                this.router.navigateByUrl(`/${slug}/dashboard/payment/payment-method`);
                return;
            }

            this.router.navigateByUrl(`/${slug}/dashboard/payment/pay/${saleId}`);
        };

        // Discard ephemeral Teya/MB Way checkout sale only when leaving to edit.
        if (saleId && this.shouldDiscardEphemeralSaleOnFailure()) {
            this.invoicingService.discardEphemeralCheckoutSale(saleId).subscribe({
                next: () => navigateAway(),
                error: () => navigateAway(),
            });
            return;
        }

        navigateAway();
    }

    private isFinalPaymentStatus(status: ITeyaPaymentStatus): boolean {
        return FINAL_PAYMENT_STATUSES.includes(status.status);
    }

    private isSuccessfulPaymentStatus(status: ITeyaPaymentStatus): boolean {
        return status.status === SUCCESSFUL_PAYMENT_STATUS;
    }

    private isAlreadyFinishedCancelError(error: any): boolean {
        if (error?.errData?.code === 'TEYA_PAYMENT_ALREADY_FINISHED') {
            return true;
        }

        const message = String(error?.errMsg || error?.message || '').toLowerCase();

        return message.includes('already finished');
    }

    cancelTeyaTerminal() {
        const saleId = this.completeInvoiceData()?.id;
        const paymentRequestId = this.activeTeyaPaymentRequestId;

        if (!saleId || !paymentRequestId || this.cancellingTeyaPayment()) {
            this.handleLivePaymentFailure('teya');
            return;
        }

        this.cancellingTeyaPayment.set(true);
        this.teyaService.cancelPayment(saleId, paymentRequestId).subscribe({
            next: (status) => {
                this.cancellingTeyaPayment.set(false);
                this.handleCancelOutcome(saleId, paymentRequestId, status);
            },
            error: (error) => {
                this.cancellingTeyaPayment.set(false);

                if (this.isAlreadyFinishedCancelError(error)) {
                    this.reconcileAfterCancelConflict(saleId, paymentRequestId);
                    return;
                }

                this.handleLivePaymentFailure(
                    'teya',
                    error?.errMsg || this.translateService.instant('GENERAL.generalErrorMsg'),
                );
            },
        });
    }

    private handleCancelOutcome(saleId: number, paymentRequestId: string, status: ITeyaPaymentStatus) {
        if (this.isSuccessfulPaymentStatus(status)) {
            this.finishLocalTeyaWait();
            this.teyaPaymentFailed.set(false);
            this.refreshSaleData();
            return;
        }

        this.handleLivePaymentFailure('teya');
    }

    private reconcileAfterCancelConflict(saleId: number, paymentRequestId: string) {
        this.teyaService.getPaymentStatus(saleId, paymentRequestId, true).subscribe({
            next: (status) => {
                this.handleCancelOutcome(saleId, paymentRequestId, status);
            },
            error: (error) => {
                this.handleLivePaymentFailure(
                    'teya',
                    error?.errMsg || this.translateService.instant('GENERAL.generalErrorMsg'),
                );
            },
        });
    }

    private finishLocalTeyaWait() {
        this.callTeyaTerminal.set(false);
        this.stopPolling$.next();
        this.activeTeyaPaymentRequestId = null;
    }

    goToStart() {
        this.invoicingService.clearAll();
        this.router.navigateByUrl('/' + this.currentStore().slug + '/dashboard/calendar');
    }

    goToSales() {
        this.router.navigateByUrl('/' + this.currentStore().slug + '/dashboard/sales');
    }

    downloadInvoice(data: any) {
        this.invoicingService.downloadInvoicePdf(data.invoicePdfUrl, data.invoiceReference);
    }

    printInvoice(data: any) {
        this.invoicingService.getInvoicePdfBlob(data.invoicePdfUrl).subscribe((blob) => {
            this.toolsService.printBlob(blob);
        });
    }

    printTicket(data: any) {
        if (!data?.invoiceTicketPdfUrl) {
            return;
        }

        this.invoicingService.getInvoicePdfBlob(data.invoiceTicketPdfUrl).subscribe((blob) => {
            this.toolsService.printBlob(blob);
        });
    }

    emailInvoice(sale: any) {
        this.invoicingService.requestEmailInvoice({
            merchantClientEmail: sale.merchantClient?.email,
            saleId: sale.id,
            saleInvoiceId: sale?.saleInvoices?.[0]?.id,
        });
    }

    openSaleDetails() {
        // Same ngx-bootstrap path as sales list / booking modal (centered modal-lg).
        this.modalService.show(SaleDetailsComponent, {
            initialState: { sale: this.completeInvoiceData() },
            class: 'modal-lg',
        });
    }
}
